import React from 'react';
import { IColumn } from '../types';
import { defaultHeaderRenderer } from '../headers';
import { CellEventsHandler } from '../formatters';
import { CellProps } from '../../types';
import { DocStatus, ShareCodeDetail, ShareCodeStatus } from 'types';
import { Badge, BadgeProps, Box, VStack } from '@chakra-ui/react';
import { addToDate, dateDiff, getDateFromString, isValidDate, now, parseDate } from 'shared-components';
import { SHARE_CODE_EXPIRY_ALERT_DAYS_DEFAULT, SHARE_CODE_EXPIRY_DAYS_DEFAULT } from 'data';

const EXPIRING_SOON_DAYS_THRESHOLD = SHARE_CODE_EXPIRY_ALERT_DAYS_DEFAULT;

const ShareCodeFormatter = (cellProps: CellProps): React.JSX.Element => {
  const {
    row: { immigrationStatusShareCode, shareCodeDetail },
  } = cellProps;

  if (!immigrationStatusShareCode || !shareCodeDetail) {
    // return { children: 'None', variant: 'transparent' };
    return <CellEventsHandler cellProps={cellProps}>{'None'}</CellEventsHandler>;
  }

  const shareCode = immigrationStatusShareCode;

  const getShareCodeStatus = ({ status, shareCodeGenerateDate }: ShareCodeDetail): ShareCodeStatus => {
    let shareCodeStatus: ShareCodeStatus;

    // check for expiry only if status is NONE
    const validDate = isValidDate(getDateFromString(shareCodeGenerateDate));

    const NOT_VERIFIED_STATUS = [
      DocStatus.AWAITING,
      DocStatus.UPLOADED_OK,
      DocStatus.UPLOADED_WITH_ERROR,
      DocStatus.REJECTED,
      DocStatus.REJECTED_WITHOUT_ALTERNATIVE,
      DocStatus.INFO_ONLY,
      DocStatus.ACKNOWLEDGED,
      DocStatus.REPLACED,
      DocStatus.DELETED,
      DocStatus.NOT_ACKNOWLEDGED,
    ];
    const STATUS_TO_CHECK_EXPIRY = [ShareCodeStatus.NOT_VERIFIED, ShareCodeStatus.NONE];

    if (status === DocStatus.APPROVED) {
      shareCodeStatus = ShareCodeStatus.VERIFIED;
    } else if (NOT_VERIFIED_STATUS.includes(status)) {
      shareCodeStatus = ShareCodeStatus.NOT_VERIFIED;
    } else {
      shareCodeStatus = ShareCodeStatus.NONE;
    }

    if (validDate && STATUS_TO_CHECK_EXPIRY.includes(shareCodeStatus)) {
      const shareCodeGenerateDateParsed = parseDate(shareCodeGenerateDate);

      const currentDate = parseDate(now());
      const expiryDate = addToDate(shareCodeGenerateDateParsed, { days: SHARE_CODE_EXPIRY_DAYS_DEFAULT });

      const daysDiff = dateDiff(currentDate, expiryDate, 'days');

      if (daysDiff <= EXPIRING_SOON_DAYS_THRESHOLD && daysDiff >= 0) {
        shareCodeStatus = ShareCodeStatus.EXPIRING_SOON;
      } else if (daysDiff < 1) {
        shareCodeStatus = ShareCodeStatus.EXPIRED;
      }
    }

    return shareCodeStatus;
  };

  const getStatus = (shareCodeStatus: ShareCodeStatus): BadgeProps => {
    switch (shareCodeStatus) {
      case ShareCodeStatus.VERIFIED: {
        return { children: 'Verified', colorScheme: 'green' };
      }
      case ShareCodeStatus.NOT_VERIFIED: {
        return { children: 'Not Verified', colorScheme: 'blue' };
      }
      case ShareCodeStatus.EXPIRING_SOON: {
        return { children: 'Expiring Soon', colorScheme: 'yellow' };
      }
      case ShareCodeStatus.EXPIRED: {
        return { children: 'Expired', colorScheme: 'red' };
      }
      default: {
        return { children: 'None', variant: 'transparent' };
      }
    }
  };

  const { colorScheme, children, variant } = getStatus(getShareCodeStatus(shareCodeDetail));

  return (
    <CellEventsHandler cellProps={cellProps}>
      <Box my={-1}>
        <VStack align="start">
          <Badge colorScheme={colorScheme} variant={variant}>
            {children}
          </Badge>
          <Box color="muted" fontSize="xs" lineHeight="4">
            {shareCode}
          </Box>
        </VStack>
      </Box>
    </CellEventsHandler>
  );
};

export const ShareCodeColumn = (): IColumn => ({
  item: {
    key: 'shareCode',
    name: 'Share Code',
    headerRenderer: defaultHeaderRenderer,
    width: 'minmax(160px, 1.25fr)',
    sortable: true,
    formatter: ShareCodeFormatter,
  },
  visible: ({ isShareCodeVisible }) => isShareCodeVisible!,
});
